import { loginRequestSchema, meResponseSchema, type LoginRequest } from '@koderek/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Anchor, Button, Group, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client';
import { AuthCard } from '../features/auth/AuthCard';
import { ResendActivation } from '../features/auth/ResendActivation';
import { errorMessage, safeReturnTo, useSetMe } from '../features/auth/session';
import { t } from '../texts/pl';

/** US-204: logowanie; po sukcesie powrót na stronę, z której przyszedł użytkownik (AC-1). */
export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setMe = useSetMe();
  const form = useForm<LoginRequest>({ resolver: zodResolver(loginRequestSchema) });
  const login = useMutation({
    mutationFn: (body: LoginRequest) => api('POST', '/auth/login', { body, schema: meResponseSchema }),
    onSuccess: (me) => {
      setMe(me);
      void navigate(safeReturnTo(params.get('powrot')), { replace: true });
    },
  });
  const notVerified = login.error instanceof ApiError && login.error.code === 'email_not_verified';

  return (
    <AuthCard title={t.login.title}>
      <form onSubmit={form.handleSubmit((values) => login.mutate(values))} noValidate>
        <Stack gap="sm">
          <TextInput label={t.fields.email} type="email" autoComplete="email" required {...form.register('email')} error={form.formState.errors.email?.message} />
          <PasswordInput label={t.fields.password} autoComplete="current-password" required {...form.register('password')} error={form.formState.errors.password?.message} />
          {login.isError && !notVerified ? (
            <Alert color="red" role="alert">
              {errorMessage(login.error)}
            </Alert>
          ) : null}
          <Button type="submit" loading={login.isPending}>
            {t.login.submit}
          </Button>
          <Group justify="space-between">
            <Anchor component={Link} to="/nie-pamietam-hasla" size="sm">
              {t.login.forgot}
            </Anchor>
            <Text size="sm">
              {t.login.noAccount}{' '}
              <Anchor component={Link} to={params.get('powrot') ? `/rejestracja?powrot=${encodeURIComponent(params.get('powrot')!)}` : '/rejestracja'}>
                {t.nav.register}
              </Anchor>
            </Text>
          </Group>
        </Stack>
      </form>
      {notVerified ? (
        <Stack gap="sm">
          <Alert color="orange" role="alert">
            {t.login.notVerified}
          </Alert>
          <ResendActivation defaultEmail={form.getValues('email')} />
        </Stack>
      ) : null}
    </AuthCard>
  );
}
